import { RemoteFile } from '@/components/file-thumbnail/utils';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';

export const copyToClipboard = (
  start: number,
  end: number,
  id: string,
  onSuccess?: () => void,
  onError?: () => void,
) => {
  //TODO : NEED SOMETHING NEW LOGIC ?!?
  const range = new Range();

  const temp = document.getElementById(id);

  if (temp) {
    range.setStart(temp, start);
    range.setEnd(temp, end);

    document.getSelection()?.removeAllRanges();
    document.getSelection()?.addRange(range);

    if (document.execCommand('copy')) {
      if (onSuccess && typeof onSuccess === 'function') {
        onSuccess();
      }
    } else {
      if (onError && typeof onError === 'function') {
        onError();
      }
    }
    document.getSelection()?.removeAllRanges();
  }
};

export const copyClipBoardText = async (text: string) => {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch (e) {
    return false;
  }
};

export const scrollToElement = (element: HTMLElement, offset = 0) => {
  if (element) {
    const elementPosition =
      element.getBoundingClientRect().top + window.scrollY;
    const offsetPosition = elementPosition - offset;
    window.scrollTo({ top: offsetPosition, behavior: 'smooth' });
  }
};

export const DOWNLOAD_FILE_NOT_OK_MESSAGE = '파일을 다운로드할 수 없습니다.';

export const downloadFile = async (file: RemoteFile) => {
  try {
    const response = await fetch(file.link);

    if (!response.ok) return DOWNLOAD_FILE_NOT_OK_MESSAGE;

    const blob = await response.blob();
    const blobUrl = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = blobUrl;
    link.download = file.name;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    // 바로 지우면 브라우저가 파일명을 정하기 전에 주소가 사라져 blob ID 로 저장됨
    setTimeout(() => URL.revokeObjectURL(blobUrl), 5_000);
    return true;
  } catch (error) {
    return `${error}` || '파일을 다운로드 하는데 실패했습니다';
  }
};

export const stripHTML = (html: string): string => {
  return html.replace(/<\/?[^>]+(>|$)/g, '');
};

export const chunkList = <T>(items?: T[], itemsPerPage = 5): T[][] => {
  if (!Array.isArray(items) || items.length === 0) return [];
  if (itemsPerPage <= 0) return [];

  const result: T[][] = [];
  for (let i = 0; i < items.length; i += itemsPerPage) {
    result.push(items.slice(i, i + itemsPerPage));
  }
  return result;
};

export const overCountText = (count: number | undefined, limitCount = 99) => {
  if (!count) return undefined;

  if (count > limitCount) return `${limitCount}+`;
  else return `${count}`;
};

export const diffTodayText = (dateText: string) => {
  if (!dateText) return dateText;

  const { t } = useTranslation();
  const now = dayjs();
  const end = dayjs(dateText);

  if (!end.isValid()) return dateText;

  const diffH = now.diff(end, 'hour');
  const diffD = now.startOf('day').diff(end.startOf('day'), 'day');
  const diffM = now.startOf('day').diff(end.startOf('day'), 'month');
  const diffY = now.startOf('day').diff(end.startOf('day'), 'year');

  if (diffH < 1) {
    return t('방금 전');
  } else if (diffH < 24) {
    return `${diffH}${t('시간 전')}`;
  } else if (diffD < 31) {
    return `${diffD}${t('일 전')}`;
  } else if (diffD < 365) {
    return `${diffM}${t('개월 전')}`;
  } else {
    return `${diffY}${t('년 전')}`;
  }
};

export type QueryParamValue =
  | string
  | number
  | boolean
  | string[]
  | undefined
  | null;

export const buildQueryParams = (
  params: Record<string, QueryParamValue>,
): string => {
  const searchParams = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value === undefined || value === null || value === '') return;
    if (Array.isArray(value)) {
      if (value.length > 0) {
        searchParams.append(key, value.join(','));
      }
    } else {
      searchParams.append(key, String(value));
    }
  });
  return searchParams.toString();
};

// 값 동등성 검사 유틸 (Date, Array(원시값 또는 Date), 원시값 지원)
export const formatFileSize = (bytes: number): string => {
  if (!Number.isFinite(bytes) || bytes < 0) return '';
  if (bytes < 1024) return `${bytes} B`;
  const kb = bytes / 1024;
  if (kb < 1024) return `${kb.toFixed(1)} KB`;
  const mb = kb / 1024;
  if (mb < 1024) return `${mb.toFixed(1)} MB`;
  const gb = mb / 1024;
  return `${gb.toFixed(2)} GB`;
};

// 값 동등성 검사 유틸 (Date, Array(원시값 또는 Date), 원시값 지원)
export const isEqualValue = (a: any, b: any): boolean => {
  // 동일한 참조 또는 원시 동일성
  if (a === b) return true;

  // Default: if either is null/undefined while other not -> not equal
  if (a == null || b == null) return false;

  // Date 비교
  if (a instanceof Date && b instanceof Date) {
    return a.getTime() === b.getTime();
  }

  // Array 비교 (원시값 또는 Date 요소 가정)
  if (Array.isArray(a) && Array.isArray(b)) {
    if (a.length !== b.length) return false;
    for (let i = 0; i < a.length; i++) {
      const ai = a[i];
      const bi = b[i];
      // 재귀적으로 처리 (각 요소가 Date 혹은 원시값일 가능성)
      if (!isEqualValue(ai, bi)) return false;
    }
    return true;
  }

  // 객체(평범한 POJO) 비교 — 얕은 비교 (필요하면 재귀로 바꿀 수 있음)
  if (typeof a === 'object' && typeof b === 'object') {
    const aKeys = Object.keys(a);
    const bKeys = Object.keys(b);
    if (aKeys.length !== bKeys.length) return false;
    for (const key of aKeys) {
      if (!isEqualValue(a[key], b[key])) return false;
    }
    return true;
  }

  // 그 외(숫자/문자 등) — 마지막 수단으로 === 사용
  return false;
};
